import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalTitle,
	ModalDescription,
	ModalFooter,
} from './Modal';
import { Button } from './Button';

/**
 * Imperative confirmation built on the app's one Modal system.
 *
 * Replaces `window.confirm()` (blocks the tab, unstyleable, looks like a
 * browser crash) and the two hand-rolled confirm dialogs, and - worse - the
 * destructive buttons that confirmed nothing at all: deleting a monitoring
 * schedule was a single click.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({
 *     title: 'Delete this schedule?',
 *     description: 'Monitoring for these accounts stops immediately.',
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *   })) { ... }
 */

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
	const [state, setState] = useState(null);
	const [typedValue, setTypedValue] = useState('');
	const resolverRef = useRef(null);

	const confirm = useCallback((opts) => {
		return new Promise((resolve) => {
			if (resolverRef.current) resolverRef.current(false);
			resolverRef.current = resolve;
			setTypedValue('');
			setState({
				title: opts.title || 'Are you sure?',
				description: opts.description || '',
				confirmLabel: opts.confirmLabel || 'Confirm',
				cancelLabel: opts.cancelLabel || 'Cancel',
				destructive: !!opts.destructive,
				confirmationText: opts.confirmationText || null,
				confirmationLabel: opts.confirmationLabel || 'Type the name to confirm',
			});
		});
	}, []);

	const settle = useCallback((result) => {
		setState(null);
		if (resolverRef.current) {
			resolverRef.current(result);
			resolverRef.current = null;
		}
	}, []);

	return (
		<ConfirmContext.Provider value={confirm}>
			{children}
			<Modal open={!!state} onOpenChange={(open) => !open && settle(false)}>
				{state && (
					<ModalContent size="sm" className="max-h-[90vh] overflow-y-auto">
						<ModalHeader>
							<ModalTitle>{state.title}</ModalTitle>
							{state.description && (
								<ModalDescription>{state.description}</ModalDescription>
							)}
						</ModalHeader>
						{state.confirmationText && (
							<label className="space-y-2 text-sm font-medium text-foreground">
								<span>{state.confirmationLabel}: <strong>{state.confirmationText}</strong></span>
								<input
									autoFocus
									value={typedValue}
									onChange={(event) => setTypedValue(event.target.value)}
									autoComplete="off"
									className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500"
								/>
							</label>
						)}
						<ModalFooter className="gap-2">
							<Button variant="secondary" onClick={() => settle(false)}>
								{state.cancelLabel}
							</Button>
							<Button
								variant={state.destructive ? 'destructive' : 'primary'}
								onClick={() => settle(true)}
								disabled={!!state.confirmationText && typedValue !== state.confirmationText}
							>
								{state.confirmLabel}
							</Button>
						</ModalFooter>
					</ModalContent>
				)}
			</Modal>
		</ConfirmContext.Provider>
	);
}

export function useConfirm() {
	const ctx = useContext(ConfirmContext);
	if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
	return ctx;
}
